import React, { useState } from "react";
import { Bell, BellOff, BellRing, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { usePushNotifications } from "../../hooks/usePushNotifications";

/**
 * PushIndicator — badge compacto que mostra o estado de push do empreendedor.
 *
 * 3 estados:
 *  - granted  + subscribed → 🟢 "Push ativo"
 *  - granted  + !subscribed (raro) → 🟠 "Reativar"
 *  - default (ainda não pediu) → 🟠 "Ativar avisos"
 *  - denied → 🔴 "Push bloqueado" (instrui o usuário a desbloquear)
 *  - unsupported → não renderiza nada
 *
 * Clique:
 *  - default/granted-not-subscribed → chama subscribe()
 *  - denied → abre tooltip explicando como desbloquear no browser
 *
 * Uso:
 *   <PushIndicator municipioId={loja.municipio_id} />
 */
export default function PushIndicator({ municipioId }) {
  const { isSupported, permission, isSubscribed, loading, subscribe } =
    usePushNotifications();
  const [explaining, setExplaining] = useState(false);

  if (!isSupported || permission === "unsupported") return null;

  const handleClick = async () => {
    if (permission === "denied") {
      setExplaining((v) => !v);
      return;
    }
    if (permission === "granted" && isSubscribed) {
      toast.success("Notificações já estão ativas neste dispositivo.");
      return;
    }
    const ok = await subscribe(municipioId);
    if (ok) {
      toast.success("Notificações ativadas — você será avisado de novos pedidos.");
    } else if (Notification.permission === "denied") {
      toast.error("Permissão negada. Desbloqueie nas configurações do navegador.");
    } else {
      toast.error("Não foi possível ativar agora — tente de novo.");
    }
  };

  // ----- variantes visuais -----
  let label, Icon, cls, title;
  if (permission === "granted" && isSubscribed) {
    label = "Push ativo";
    Icon = BellRing;
    cls = "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100";
    title = "Você receberá notificações de novos pedidos neste dispositivo.";
  } else if (permission === "denied") {
    label = "Push bloqueado";
    Icon = BellOff;
    cls = "bg-red-50 border-red-200 text-red-700 hover:bg-red-100";
    title = "Desbloqueie notificações nas configurações do navegador.";
  } else {
    label = "Ativar avisos";
    Icon = Bell;
    cls = "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100";
    title = "Receber notificação de novo pedido (recomendado).";
  }

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={loading}
        title={title}
        data-testid="delivery-push-indicator"
        data-state={
          permission === "granted" && isSubscribed
            ? "active"
            : permission === "denied"
            ? "blocked"
            : "inactive"
        }
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition ${cls} ${
          loading ? "opacity-60 cursor-wait" : ""
        }`}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Icon className="w-3.5 h-3.5" />
        )}
        <span className="hidden sm:inline">{label}</span>
        {permission === "granted" && isSubscribed && (
          <span
            className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse ml-0.5"
            aria-hidden
          />
        )}
      </button>

      {/* Tooltip explicativo quando bloqueado e usuário clicou */}
      {explaining && permission === "denied" && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-white border border-[var(--border)] rounded-lg shadow-lg p-3 z-50 text-xs"
          data-testid="delivery-push-blocked-help"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[var(--text-primary)] mb-1">
                Notificações bloqueadas
              </p>
              <p className="text-[var(--text-secondary)] leading-snug mb-2">
                Para receber avisos de novos pedidos, abra as configurações
                do navegador → Permissões → Notificações e libere para este
                site.
              </p>
              <button
                onClick={() => setExplaining(false)}
                className="text-[var(--primary)] underline"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
